/**
 * Centralized Query Key Factory for TanStack Query
 *
 * Benefits:
 * - Consistent key structure across all queries
 * - Type-safe query key generation
 * - Easy cache invalidation
 * - Prevents cache thrashing from inconsistent key ordering
 *
 * Usage:
 * - Use these factories in all useQuery hooks
 * - Use for targeted cache invalidation
 * - Ensures consistent parameter ordering
 */

export const sentryQueryKeys = {
  // Base keys
  all: ['sentry'] as const,

  // Events queries
  events: {
    all: ['sentry', 'events'] as const,
    list: (filters: {
      projectId: string
      environment?: string | null
      startDate?: string | null
      endDate?: string | null
      workstream?: string | null
      searchQuery?: string | null
      cursor?: string | null
    }) => ['sentry', 'events', 'list', filters] as const,
  },

  // Priority counts
  priorityCounts: (filters: {
    projectId: string
    startDate: string
    endDate: string
    workstream?: string | null
    environment?: string | null
    searchQuery?: string | null
  }) => ['sentry', 'priority-counts', filters] as const,

  // Severity percentage
  severityPercentage: (filters: {
    projectId: string
    startDate: string
    endDate: string
    workstream?: string | null
    environment?: string | null
    searchQuery?: string | null
  }) => ['sentry', 'severity-percentage', filters] as const,

  // Trend graph
  trendGraph: (filters: {
    projectId: string
    statsPeriod: string
    severity: 'high' | 'medium' | 'low' | 'total'
    environment?: string | null
    workstream?: string | null
  }) => ['sentry', 'trend-graph', filters] as const,

  // Issue details
  issueDetails: (issueId: string) => ['sentry', 'issue-details', issueId] as const,
}

export const cloudWatchQueryKeys = {
  // Base keys
  all: ['cloudwatch'] as const,

  // Logs queries
  logs: (filters: {
    workstream: string
    statusCodes?: string | null
    startDate?: string | null
    endDate?: string | null
    environment?: string | null
    cursor?: string | null
    limit?: number | null
  }) => ['cloudwatch', 'logs', filters] as const,

  // Counts queries
  counts: (filters: {
    workstream: string
    statusCodes?: string | null
    startDate?: string | null
    endDate?: string | null
    environment?: string | null
  }) => ['cloudwatch', 'counts', filters] as const,
}
